import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, race, timer } from 'rxjs';
import { map, catchError, switchMap, tap, concatMap, timeout } from 'rxjs/operators';
import { UserActions } from './user.actions';
import { UserApiService } from '@app/core/api/user';
import { LoggerService } from '@app/core/infrastructure';
import { NotificationFacade } from '@app/store/notification';
import { Capacitor } from '@capacitor/core';
import { PlatformType } from '@app/core/api/notification';
import { Store } from '@ngrx/store';
import { NotificationActions } from '@app/store/notification/notification.actions';
import { filter, take } from 'rxjs/operators';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userApiService = inject(UserApiService);
  private readonly logger = inject(LoggerService).scope('UserEffects');
  private readonly notificationFacade = inject(NotificationFacade);
  private readonly store = inject(Store);
  
  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadProfile),
      switchMap(() =>
        this.userApiService.getMe().pipe(
          map(profile => UserActions.loadProfileSuccess({ profile })),
          catchError(error => {
            this.logger.error('Load profile failed:', error);
            return of(UserActions.loadProfileFailure({ error: error.message }));
          })
        )
      )
    )
  );

  createOrSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.createOrSync),
      switchMap(() =>
        this.userApiService.createOrSyncMe().pipe(
          map(profile => UserActions.createOrSyncSuccess({ profile })),
          catchError(error => {
            this.logger.error('Create or sync failed:', error);
            return of(UserActions.createOrSyncFailure({ error: error.message }));
          })
        )
      )
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateProfile),
      switchMap(({ data }) =>
        this.userApiService.updateMe(data).pipe(
          map(profile => UserActions.updateProfileSuccess({ profile })),
          catchError(error => {
            this.logger.error('Update profile failed:', error);
            return of(UserActions.updateProfileFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deletePersonalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deletePersonalData),
      switchMap(() =>
        this.userApiService.deletePersonalData().pipe(
          map(() => UserActions.deletePersonalDataSuccess()),
          catchError(error => {
            this.logger.error('Delete personal data failed:', error);
            return of(UserActions.deletePersonalDataFailure({ error: error.message }));
          })
        )
      )
    )
  );

  deleteAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteAccount),
      tap(() => {
        // Unregister token BEFORE deleting account
        const platform = (Capacitor.isNativePlatform() 
          ? Capacitor.getPlatform() 
          : 'web') as PlatformType;
        this.logger.debug('Unregistering token before account deletion');
        this.notificationFacade.unregisterToken(platform, true);
      }),
      // Wait for unregister to complete (with 5 second timeout)
      concatMap(() => 
        race(
          this.actions$.pipe(
            ofType(NotificationActions.unregisterTokenSuccess, NotificationActions.unregisterTokenFailure),
            take(1)
          ),
          timer(5000).pipe(
            map(() => NotificationActions.unregisterTokenFailure({ 
              error: 'Token unregister timeout (5s)' 
            }))
          )
        )
      ),
      // Only proceed with account deletion if token was successfully unregistered
      switchMap((action) => {
        if (action.type === NotificationActions.unregisterTokenSuccess.type) {
          this.logger.debug('Token unregistered successfully, proceeding with account deletion');
          return this.userApiService.deleteMe().pipe(
            map(() => UserActions.deleteAccountSuccess()),
            catchError(error => {
              this.logger.error('Delete account failed:', error);
              return of(UserActions.deleteAccountFailure({ error: error.message }));
            })
          );
        } else {
          // Token unregister failed or timed out - abort account deletion
          this.logger.error('Token unregister failed, aborting account deletion');
          return of(UserActions.deleteAccountFailure({ 
            error: 'Failed to unregister push token. Please check your connection and try again.' 
          }));
        }
      })
    )
  );

  // Log successful operations
  logSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserActions.loadProfileSuccess,
        UserActions.createOrSyncSuccess,
        UserActions.updateProfileSuccess
      ),
      tap(action => this.logger.debug('Success:', action.type))
    ),
    { dispatch: false }
  );
}
